/* eslint-disable rover/no-platform-specific-globals-or-imports */
import React, { FC, useEffect } from 'react';
import { matchRoutes, renderRoutes, RouteConfig } from 'react-router-config';
import { BrowserRouter } from 'react-router-dom';
import { useHydrate } from '@tanstack/react-query';
import memoize from 'lodash-es/memoize';

import { decodeHtmlNodes } from '@rover/utilities/json';

import gtmScript from './scripts/gtm.script';

type Props = {
  routes: RouteConfig[];
};

const dehydratedState = decodeHtmlNodes(window.__REACT_QUERY_STATE__ || {}); // eslint-disable-line no-underscore-dangle, @typescript-eslint/no-unused-vars, @typescript-eslint/no-shadow
const findClosestAnchor = (element: HTMLElement): HTMLAnchorElement | null => {
  let e: HTMLElement | null = element;
  while (e && e.tagName !== 'A') {
    e = e.parentElement;
    if (e && e instanceof HTMLAnchorElement) {
      return e;
    }
  }
  return null;
};

const App: FC<Props> = ({ routes }) => {
  const findMatchingRoute = memoize((pathname) => matchRoutes(routes, pathname)[1]);
  const preloadRoute = memoize(
    (event: MouseEvent) => {
      let anchor;
      if (event.target instanceof HTMLElement) {
        anchor = findClosestAnchor(event.target);
      }
      if (anchor) {
        const { pathname } = anchor;
        if (pathname) {
          const match = findMatchingRoute(pathname);
          if (match && match.route && match.route.component && match.route.component.preload) {
            match.route.component.preload();
          }
        }
      }
    },
    (event) => event.target
  );

  useEffect(() => {
    document.addEventListener('mouseover', preloadRoute);
    gtmScript();

    return () => {
      document.removeEventListener('mouseover', preloadRoute);
    };
  });

  useHydrate(dehydratedState ?? {});

  return <BrowserRouter>{renderRoutes(routes)}</BrowserRouter>;
};

export default App;
